import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Delete, Plus, Edit } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { specialAwardsService } from '../../../services/specialAwards.service';
import { getFairs } from '../../../services/fairs.services';
import { Fair } from '../../../types/fair.types';
import { SpecialAwardWithDetails } from '../../../types/specialAwards.types';
import { db } from '../../../firebase';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from 'firebase/firestore';

interface ProjectOption {
  id: string;
  name: string;
  memberIds: string[];
  studentNames: string[]; // Add student names to project option
}

const SpecialAwardsContent: React.FC = () => {
  const { authStatus } = useAuth();
  const [fairs, setFairs] = useState<Fair[]>([]);
  const [selectedFairId, setSelectedFairId] = useState<string>('');
  const [awards, setAwards] = useState<SpecialAwardWithDetails[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAward, setEditingAward] =
    useState<SpecialAwardWithDetails | null>(null);
  const [awardName, setAwardName] = useState('');
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);

  // Fetch all fairs
  useEffect(() => {
    const fetchFairs = async () => {
      if (!authStatus.user?.uid) return;

      try {
        // Get user's region first
        const userDoc = await getDoc(doc(db, 'users', authStatus.user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const regionId = userData.regionId;
          if (regionId) {
            const fairsData = await getFairs(regionId);
            setFairs(fairsData);
          }
        }
      } catch (err) {
        console.error('Error fetching fairs:', err);
        setError('Failed to load fairs');
      }
    };

    fetchFairs();
  }, [authStatus.user?.uid]);

  const fetchProjectsAndStudents = useCallback(async () => {
    if (!selectedFairId) return;

    setLoading(true);
    try {
      // Fetch projects for this fair
      const projectsRef = collection(db, 'projects');
      const projectsQuery = query(
        projectsRef,
        where('fairId', '==', selectedFairId)
      );
      const projectsSnapshot = await getDocs(projectsQuery);

      const projectsList: ProjectOption[] = [];

      // Fetch projects with their student names
      for (const projectDoc of projectsSnapshot.docs) {
        const projectData = projectDoc.data();
        const memberIds = projectData.memberIds || [];

        // Fetch student names for this project
        const studentNames: string[] = [];
        for (const memberId of memberIds) {
          try {
            const userDoc = await getDoc(doc(db, 'users', memberId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              const fullName =
                `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
              if (fullName) {
                studentNames.push(fullName);
              }
            }
          } catch (err) {
            console.error(`Error fetching student ${memberId}:`, err);
          }
        }

        projectsList.push({
          id: projectDoc.id,
          name: projectData.name || 'Unknown Project',
          memberIds: memberIds,
          studentNames: studentNames,
        });
      }

      setProjects(projectsList);
    } catch (err) {
      console.error('Error fetching projects and students:', err);
      setError('Failed to load projects and students');
    } finally {
      setLoading(false);
    }
  }, [selectedFairId]);

  const fetchAwards = useCallback(async () => {
    if (!selectedFairId) return;

    try {
      const awardsData =
        await specialAwardsService.getSpecialAwardsWithDetails(selectedFairId);
      setAwards(awardsData);
    } catch (err) {
      console.error('Error fetching awards:', err);
      setError('Failed to load special awards');
    }
  }, [selectedFairId]);

  // Fetch projects and students when fair is selected
  useEffect(() => {
    if (selectedFairId) {
      fetchProjectsAndStudents();
      fetchAwards();
    } else {
      setProjects([]);
      setAwards([]);
    }
  }, [selectedFairId, fetchProjectsAndStudents, fetchAwards]);

  const handleOpenDialog = (award?: SpecialAwardWithDetails) => {
    if (award) {
      setEditingAward(award);
      setAwardName(award.awardName);
      setSelectedProjectIds(award.projectIds);
    } else {
      setEditingAward(null);
      setAwardName('');
      setSelectedProjectIds([]);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAward(null);
    setAwardName('');
    setSelectedProjectIds([]);
    setError('');
  };

  const handleSubmit = async () => {
    if (!awardName.trim()) {
      setError('Please enter an award name');
      return;
    }

    if (selectedProjectIds.length === 0) {
      setError('Please select at least one project');
      return;
    }

    if (!selectedFairId) {
      setError('Please select a fair');
      return;
    }

    try {
      setError('');
      setLoading(true);

      // When creating/updating, only use projectIds, not individual studentIds
      // The students will be automatically included from the projects
      if (editingAward) {
        await specialAwardsService.updateSpecialAward(
          editingAward.id,
          awardName,
          selectedProjectIds,
          [] // Empty array for studentIds - only projects are selected
        );
        setSuccess('Special award updated successfully');
      } else {
        if (!authStatus.user?.uid) {
          setError('User not authenticated');
          return;
        }

        await specialAwardsService.createSpecialAward(
          selectedFairId,
          awardName,
          selectedProjectIds,
          [], // Empty array for studentIds - only projects are selected
          authStatus.user.uid
        );
        setSuccess('Special award created successfully');
      }

      await fetchAwards();
      handleCloseDialog();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving award:', err);
      setError('Failed to save special award');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (awardId: string) => {
    if (
      !window.confirm('Are you sure you want to delete this special award?')
    ) {
      return;
    }

    try {
      await specialAwardsService.deleteSpecialAward(awardId);
      setSuccess('Special award deleted successfully');
      await fetchAwards();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting award:', err);
      setError('Failed to delete special award');
    }
  };

  const handleProjectToggle = (projectId: string) => {
    setSelectedProjectIds((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId]
    );
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom color="primary" fontWeight="bold">
          Special Awards Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Create and manage special awards for fairs
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select Fair</InputLabel>
          <Select
            value={selectedFairId}
            onChange={(e) => setSelectedFairId(e.target.value)}
            label="Select Fair"
          >
            {fairs.map((fair) => (
              <MenuItem key={fair.id} value={fair.id}>
                {fair.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedFairId && (
          <Button
            variant="contained"
            startIcon={<Plus />}
            onClick={() => handleOpenDialog()}
            sx={{ mb: 2 }}
          >
            Create Special Award
          </Button>
        )}
      </Paper>

      {selectedFairId && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Special Awards for Selected Fair
          </Typography>
          {loading && !awards.length ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : awards.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3 }}>
              No special awards created yet. Click "Create Special Award" to add
              one.
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Award Name</TableCell>
                    <TableCell>Projects</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {awards.map((award) => (
                    <TableRow key={award.id}>
                      <TableCell>
                        <Typography fontWeight="medium">
                          {award.awardName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {award.projects.length > 0 ? (
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {award.projects.map((project) => (
                              <Chip
                                key={project.id}
                                label={project.name}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography color="text.secondary" variant="body2">
                            None
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(award)}
                          color="primary"
                        >
                          <Edit size={16} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(award.id)}
                          color="error"
                        >
                          <Delete size={16} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingAward ? 'Edit Special Award' : 'Create Special Award'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Award Name"
              value={awardName}
              onChange={(e) => setAwardName(e.target.value)}
              sx={{ mb: 3 }}
              required
            />

            <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
              Select Projects
            </Typography>
            <Box
              sx={{
                maxHeight: 400,
                overflow: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
              }}
            >
              {loading ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : projects.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  No projects found for this fair
                </Typography>
              ) : (
                projects.map((project) => (
                  <Box
                    key={project.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      py: 1,
                    }}
                  >
                    <Checkbox
                      checked={selectedProjectIds.includes(project.id)}
                      onChange={() => handleProjectToggle(project.id)}
                      sx={{ mt: 0.5 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight="medium">
                        {project.name}
                      </Typography>
                      {project.studentNames.length > 0 && (
                        <Typography
                          variant="body2"
                          sx={{
                            fontStyle: 'italic',
                            color: 'text.secondary',
                            mt: 0.5,
                          }}
                        >
                          {project.studentNames.join(', ')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !awardName.trim()}
          >
            {loading ? (
              <CircularProgress size={20} />
            ) : editingAward ? (
              'Update'
            ) : (
              'Create'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SpecialAwardsContent;
